import { useState, useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as exifr from 'exifr';
import imageCompression from 'browser-image-compression';
import type { CreatePhotoTagInput, PhotoTagEntityType } from '@peles/shared';
import * as photosApi from '../api/photos';
import type { S3PreImportedFile } from './useGooglePhotos';

const COMPRESSION_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 2048,
  useWebWorker: true,
  preserveExif: true,
};

/**
 * How many files may sit in the queue before a bulk import is made to wait.
 * Each one is an image held in memory until its turn comes.
 */
const MAX_QUEUED = 20;

async function compressImage(file: File): Promise<File> {
  // Skip non-image or already small files
  const isImage = file.type.startsWith('image/');
  const isSmall = file.size <= COMPRESSION_OPTIONS.maxSizeMB * 1024 * 1024;
  if (!isImage || isSmall) return file;

  // Skip formats that don't compress well (GIF may be animated)
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return file;

  try {
    const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
    // Only use compressed version if it's actually smaller
    return compressed.size < file.size ? compressed : file;
  } catch {
    // If compression fails, upload original
    return file;
  }
}

export interface UploadQueueItem {
  id: string;
  fileName: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
  uploadPct?: number;
}

export interface UploadProgress {
  completed: number;
  failed: number;
  total: number;
}

export interface UploadPublicFlags {
  isPublic?: boolean;
  showOnLanding?: boolean;
}

interface QueueEntry {
  id: string;
  file: File;
  tags?: CreatePhotoTagInput[];
  publicFlags?: UploadPublicFlags;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
  uploadPct?: number;
}

function haversineDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface VenueForMatch {
  id: string;
  name: string;
  latitude?: number | null;
  longitude?: number | null;
}

let nextId = 0;

export function useUploadQueue(venues?: VenueForMatch[]) {
  const queryClient = useQueryClient();
  const [renderQueue, setRenderQueue] = useState<QueueEntry[]>([]);
  const entriesRef = useRef<QueueEntry[]>([]);
  const processingRef = useRef(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync helper: update both ref and state
  const sync = useCallback(() => {
    setRenderQueue([...entriesRef.current]);
  }, []);

  const processQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const next = entriesRef.current.find((item) => item.status === 'pending');
      if (!next) break;

      // Mark as uploading
      next.status = 'uploading';
      sync();

      try {
        // Check if the file was already uploaded to S3 by the backend
        // (e.g. Google Photos video import via POST /google-photos/import-media).
        const preImport = (next.file as S3PreImportedFile)._s3Import;

        // Compress image before upload (max 2MB, 2048px)
        const fileToUpload = preImport ? next.file : await compressImage(next.file);

        let s3Key: string;
        if (preImport) {
          s3Key = preImport.s3Key;
        } else {
          const presigned = await photosApi.getPresignedUploadUrl({
            fileName: next.file.name,
            mimeType: fileToUpload.type,
          });
          s3Key = presigned.s3Key;
          await photosApi.uploadFileToS3(presigned.uploadUrl, fileToUpload, (pct) => {
            next.uploadPct = pct;
            sync();
          });
        }

        // Extract EXIF GPS and date-taken metadata
        let latitude: number | undefined;
        let longitude: number | undefined;
        let takenAt: string | undefined;
        try {
          const gps = await exifr.gps(next.file);
          if (gps) {
            latitude = gps.latitude;
            longitude = gps.longitude;
          }
          const exif = await exifr.parse(next.file, ['DateTimeOriginal']);
          if (exif?.DateTimeOriginal instanceof Date) {
            takenAt = exif.DateTimeOriginal.toISOString();
          }
        } catch {
          // No EXIF data available — skip silently
        }

        // Auto-tag with nearest venue if GPS is available
        const autoTags: CreatePhotoTagInput[] = [...(next.tags || [])];
        if (latitude != null && longitude != null && venues?.length) {
          let closest: VenueForMatch | null = null;
          let closestDist = Infinity;
          for (const v of venues) {
            if (v.latitude != null && v.longitude != null) {
              const d = haversineDistanceKm(latitude, longitude, v.latitude, v.longitude);
              if (d < closestDist) {
                closestDist = d;
                closest = v;
              }
            }
          }
          if (closest && closestDist < 0.5) {
            autoTags.push({ entityType: 'VENUE' as PhotoTagEntityType, entityId: closest.id });
          }
        }

        // The batch's tags and the GPS-matched venue can name the same thing, and
        // a duplicate (type, id) pair violates the tag uniqueness constraint —
        // which rolls back the whole photo, not just the tag.
        const uniqueTags = autoTags.filter(
          (tag, i) =>
            autoTags.findIndex(
              (t) => t.entityType === tag.entityType && t.entityId === tag.entityId,
            ) === i,
        );

        await photosApi.createPhoto({
          s3Key,
          fileName: next.file.name,
          mimeType: fileToUpload.type,
          fileSizeBytes: preImport ? preImport.fileSizeBytes : fileToUpload.size,
          latitude,
          longitude,
          takenAt,
          tags: uniqueTags.length > 0 ? uniqueTags : undefined,
          isPublic: next.publicFlags?.isPublic,
          showOnLanding: next.publicFlags?.showOnLanding,
        });

        next.status = 'done';
        sync();
        queryClient.invalidateQueries({ queryKey: ['photos'] });
      } catch (err) {
        next.status = 'error';
        next.error = err instanceof Error ? err.message : 'Upload failed';
        sync();
      }
    }

    processingRef.current = false;
  }, [queryClient, sync]);

  const addFiles = useCallback(
    (files: File[], tags?: CreatePhotoTagInput[], publicFlags?: UploadPublicFlags) => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
        clearTimerRef.current = null;
      }

      // Remove finished items from previous batch
      entriesRef.current = entriesRef.current.filter(
        (item) => item.status === 'pending' || item.status === 'uploading',
      );

      const newEntries: QueueEntry[] = files.map((file) => ({
        id: String(++nextId),
        file,
        tags,
        publicFlags,
        status: 'pending' as const,
      }));

      entriesRef.current.push(...newEntries);
      sync();

      // Kick off processing
      processQueue();
    },
    [processQueue, sync],
  );

  /**
   * Resolves once the queue has room for more files.
   *
   * ⚠️ **This is what stops a large import eating the tab.** Every file waiting
   * in the queue is a whole image held in memory, and the queue is filled by
   * whoever is handing files in — a Google Photos import can produce them far
   * faster than they upload. Two thousand photographs queued at once is several
   * gigabytes of blobs, and the tab dies with every one of them still unsaved,
   * because nothing is written until its own turn comes round. Callers feeding
   * the queue in bulk await this between files so the backlog stays bounded and
   * memory stays flat however many were picked.
   */
  const waitForCapacity = useCallback(async (): Promise<void> => {
    const waiting = () =>
      entriesRef.current.filter((item) => item.status === 'pending' || item.status === 'uploading')
        .length;
    while (waiting() >= MAX_QUEUED) {
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
  }, []);

  const clear = useCallback(() => {
    entriesRef.current = [];
    sync();
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, [sync]);

  // Auto-clear queue 3 seconds after all uploads finish
  const isUploading = renderQueue.some(
    (item) => item.status === 'pending' || item.status === 'uploading',
  );
  const hasItems = renderQueue.length > 0;

  useEffect(() => {
    if (hasItems && !isUploading) {
      clearTimerRef.current = setTimeout(() => {
        entriesRef.current = [];
        sync();
      }, 3000);
      return () => {
        if (clearTimerRef.current) {
          clearTimeout(clearTimerRef.current);
          clearTimerRef.current = null;
        }
      };
    }
  }, [hasItems, isUploading, sync]);

  const progress: UploadProgress = {
    completed: renderQueue.filter((item) => item.status === 'done').length,
    failed: renderQueue.filter((item) => item.status === 'error').length,
    total: renderQueue.length,
  };

  const queueItems: UploadQueueItem[] = renderQueue.map(({ id, file, status, error }) => ({
    id,
    fileName: file.name,
    status,
    error,
  }));

  return { addFiles, waitForCapacity, queue: queueItems, progress, isUploading, clear };
}
